import styled from "@emotion/styled";
import {
  FeaturedApp,
  FooterBackButton,
  FooterNextButton,
  FooterContainer,
  StepTitle,
  WizardContentWrapper,
  FooterSkipButton,
  FooterButtonGroup,
  StyledStepTitleWrapper,
} from "@/components/onboarding/components/welcome-wizard/welcome-wizard.styles";
import AppStoreBadge from "@/assets/images/Download_on_the_Mac_App_Store_Badge_US-UK_RGB_blk_092917.svg";
import type {
  StepProps,
  StepMap,
} from "@/components/onboarding/components/welcome-wizard/welcome-wizard.types";
import { DropTargetStep } from "./drop-target-step";
import { MDSIconButton, MDSText, MDSTextVariant, MDSTextWeight, MDSIconKind } from "@/mds";
import { StepGroups } from "@/components/onboarding/components/welcome-wizard/welcome-wizard.config";
import { StepContentWithTabs } from "@/components/onboarding/components/welcome-wizard/tabs";
import ExporterUsageGif from "@/assets/images/onboarding/exporter-usage.gif";
import ExporterLogo from "@/assets/images/onboarding/exporter-logo.png";
import { useAppStore } from "@/store";
import { clientEnvModule } from "@/modules/client-env";
import { observer } from "mobx-react-lite";
import { AppleNotesOneClickStepDesktop } from "./apple-notes-one-click-step-desktop";
import { AppleNotesOneClickStepWeb } from "./apple-notes-one-click-step-web";

// Wrapper component that checks feature flag and renders appropriate flow
const AppleNotesOneClickStep: React.FC<StepProps> = observer(props => {
  const { store } = useAppStore();
  const isDesktop = clientEnvModule.isDesktop();
  const isOneClickImportEnabled = store.spaceAccountFeatureFlags.getFlagStatus("one-click-import");

  if (!isOneClickImportEnabled) {
    return <AppleNotesStep1 {...props} />;
  }

  if (isDesktop) {
    return <AppleNotesOneClickStepDesktop {...props} />;
  }

  return <AppleNotesOneClickStepWeb {...props} />;
});

const AppleNotesStep1: React.FC<StepProps> = ({
  context,
  handleTabSelect,
  onNavToStep,
  onSkip,
  onClose,
}) => (
  <WizardContentWrapper>
    <StyledStepTitleWrapper>
      <StepTitle>Import Notes</StepTitle>
      {context.wizardType === "import" && (
        <MDSIconButton iconKind={MDSIconKind.Exit} onClick={onClose} />
      )}
    </StyledStepTitleWrapper>
    <StepContentWithTabs
      selectedAppId={context?.selectedAppId || null}
      onAppSelect={handleTabSelect}
      tabConfig={StepGroups["IMPORT"].tabConfig}
    >
      <MDSText variant={MDSTextVariant.ParagraphSm} weight={MDSTextWeight.Regular}>
        Step 1 of 3:
        <br />
        Download the Apple Notes Exporter app. You&apos;ll use this to export your Apple Notes in
        the next step.
      </MDSText>
      <FeaturedApp>
        <img src={ExporterLogo} width="94" height="94" />
        <div style={{ marginTop: 8 }}>
          <MDSText variant={MDSTextVariant.TextM} weight={MDSTextWeight.SemiBold}>
            Exporter
          </MDSText>
        </div>
        <div style={{ marginBottom: 15 }}>
          <MDSText>Export Apple Notes</MDSText>
        </div>
        <a
          href="https://apps.apple.com/us/app/exporter/id1099120373?mt=12"
          target="_blank"
          rel="noreferrer"
          style={{ marginBottom: -5 }}
        >
          <img alt="Download on the Mac App Store" src={AppStoreBadge} width="156" height="40" />
        </a>
      </FeaturedApp>
    </StepContentWithTabs>
    <AppleNotesFooter onNext={() => onNavToStep?.("apple-notes-2")} onSkip={onSkip} />
  </WizardContentWrapper>
);

const AppleNotesStep2: React.FC<StepProps> = ({
  onNavToStep,
  onSkip,
  onClose,
  handleTabSelect,
  context,
}) => (
  <WizardContentWrapper>
    <StyledStepTitleWrapper>
      <StepTitle>Import Notes</StepTitle>
      {context.wizardType === "import" && (
        <MDSIconButton iconKind={MDSIconKind.Exit} onClick={onClose} />
      )}
    </StyledStepTitleWrapper>
    <StepContentWithTabs
      selectedAppId={context?.selectedAppId || null}
      onAppSelect={handleTabSelect}
      tabConfig={StepGroups["IMPORT"].tabConfig}
    >
      <MDSText variant={MDSTextVariant.ParagraphSm} weight={MDSTextWeight.Regular}>
        Step 2 of 3:
        <br />
        Export your Apple Notes to a folder on your computer
      </MDSText>
      <StyledExporterImage src={ExporterUsageGif} width="370" height="229" />
    </StepContentWithTabs>
    <AppleNotesFooter
      onBack={() => onNavToStep?.("apple-notes-1")}
      onNext={() => onNavToStep?.("apple-notes-3")}
      onSkip={onSkip}
    />
  </WizardContentWrapper>
);

const AppleNotesStep3: React.FC<StepProps> = ({
  onNavToStep,
  onClose,
  onSkip,
  handleTabSelect,
  context,
}) => (
  <WizardContentWrapper>
    <StyledStepTitleWrapper>
      <StepTitle>Import Notes</StepTitle>
      {context.wizardType === "import" && (
        <MDSIconButton iconKind={MDSIconKind.Exit} onClick={onClose} />
      )}
    </StyledStepTitleWrapper>
    <StepContentWithTabs
      selectedAppId={context?.selectedAppId || null}
      onAppSelect={handleTabSelect}
      tabConfig={StepGroups["IMPORT"].tabConfig}
    >
      <MDSText variant={MDSTextVariant.ParagraphSm} weight={MDSTextWeight.Regular}>
        Step 3 of 3: <br />
        Import your notes into Mem by dragging the folder into the area below.
      </MDSText>
      <DropTargetStep
        onDrop={() => {
          if (context.wizardType === "welcome") onNavToStep?.("download-apps");
          if (context.wizardType === "import") onClose?.();
        }}
      />
    </StepContentWithTabs>
    <AppleNotesFooter
      onBack={() => onNavToStep?.("apple-notes-2")}
      onNext={context.wizardType === "welcome" ? () => onNavToStep?.("download-apps") : undefined}
      onSkip={onSkip}
    />
  </WizardContentWrapper>
);

interface AppleNotesFooterProps {
  onNext?: () => void;
  onSkip?: () => void;
  onBack?: () => void;
}

const AppleNotesFooter = ({ onNext, onSkip, onBack }: AppleNotesFooterProps) => (
  <FooterContainer align={onSkip ? "space-between" : "flex-end"}>
    {onSkip && (
      <FooterSkipButton onClick={onSkip} isDisabled={!onSkip}>
        Skip for now
      </FooterSkipButton>
    )}
    <FooterButtonGroup>
      {onBack && (
        <FooterBackButton onClick={onBack} isDisabled={!onBack}>
          Back
        </FooterBackButton>
      )}
      {onNext && (
        <FooterNextButton onClick={onNext} isDisabled={!onNext}>
          Next step
        </FooterNextButton>
      )}
    </FooterButtonGroup>
  </FooterContainer>
);

export const AppleNotesStepsMap: StepMap = {
  "apple-notes-1": {
    id: "apple-notes-1",
    group: "import-cta",
    render: ({ onNavToStep, onSkip, handleTabSelect, context, onClose }) => (
      <AppleNotesOneClickStep
        onNavToStep={onNavToStep}
        onSkip={onSkip}
        handleTabSelect={handleTabSelect}
        context={context}
        onClose={onClose}
      />
    ),
  },
  "apple-notes-2": {
    id: "apple-notes-2",
    group: "import-cta",
    render: ({ onNavToStep, onSkip, handleTabSelect, context, onClose }) => (
      <AppleNotesStep2
        onNavToStep={onNavToStep}
        onSkip={onSkip}
        handleTabSelect={handleTabSelect}
        context={context}
        onClose={onClose}
      />
    ),
  },
  "apple-notes-3": {
    id: "apple-notes-3",
    group: "import-cta",
    render: ({ onNavToStep, onSkip, handleTabSelect, context, onClose }) => (
      <AppleNotesStep3
        onNavToStep={onNavToStep}
        onClose={onClose}
        onSkip={onSkip}
        handleTabSelect={handleTabSelect}
        context={context}
      />
    ),
  },
};

const StyledExporterImage = styled.img`
  display: flex;
  align-self: center;
  box-shadow:
    0px 1px 2px 0px rgba(0, 0, 0, 0.2),
    0px 15px 30px 3px rgba(0, 0, 0, 0.2);
  border-radius: ${({ theme }) => theme.borderRadius.medium};
`;
